import React, { useState } from "react";
import { FormattedHTMLMessage } from "react-intl";
import { SelectedOrg, SearchOverlay } from "@jslib/OrgSearch";
import { useFlag } from "@/components/FeatureFlags/hooks";
import { dashed } from "../../../lib/utils/strings";
import { FormFieldLayout } from "../FormFieldLayout/FormFieldLayout";
import { TypeaheadComponent, TypeaheadProps } from "../../FormInputs/Typeahead/TypeaheadComponent";
import { Locale, VerificationService } from "../../../lib/types/types";
import { getOptions } from "../../../options/options";
import { getLocaleSafely } from "../../../lib/intl/intl";
import { getSafe } from "../../../lib/utils/getSafe";
import { isCountrySelectionNeeded } from "../Country/countryUtils";

// Extend TypeaheadProps to pass along things like required, onKeyUp, etc.
// Omit errorId, isErrored, etc because those are handled within this component
export interface FormFieldTypeaheadComponentProps
  extends Omit<
    TypeaheadProps,
    | "inputHtmlId"
    | "id"
    | "errorId"
    | "isErrored"
    | "locale"
    | "programId"
    | "country"
    | "orgSearchUrl" // omit b/c this component (FormFieldTypeaheadComponent) has a fallback so orgSearchUrl is optional
  > {
  fieldId: string; // should be camelCase
  verificationService: VerificationService;
  label: React.ReactNode; // Label is always required for screen readers
  showPlaceholderAndHideLabel?: boolean;
  errorMessage?: React.ReactNode;
  explanation?: string | JSX.Element;
  orgSearchUrl: string;
}

/**
 * Generic typeahead field component.
 * In the JSLIB, a field has a label, inline error message, and an input of some kind.
 */
export const FormFieldTypeahead = ({
  fieldId,
  value,
  verificationService,
  onChange,
  placeholder,
  label,
  errorMessage,
  showPlaceholderAndHideLabel = false,
  isRequired = true,
  explanation = "",
  orgSearchUrl,
  urlAddSchoolForm,
  disabled = false,
}: FormFieldTypeaheadComponentProps) => {
  const labelHtmlId = `sid-${dashed(fieldId)}`;
  const errorHtmlId = `sid-${dashed(fieldId)}-error`;
  const { programId, programTheme, verificationResponse, viewModel } = verificationService;
  const countryChoiceValue = getSafe(() => viewModel.countryChoice.value);
  const needToPickCountry = isCountrySelectionNeeded(programTheme, countryChoiceValue);
  const locale: Locale = getLocaleSafely(viewModel, verificationResponse);
  const [autoFocus, setAutoFocus] = useState(false);
  const showSearchOverlay = useFlag("org-search-overlay") === "overlay";
  const showSelectedOrgBox = showSearchOverlay || useFlag("org-selected-display") === "box-display";

  return (
    <SearchOverlay enabled={showSearchOverlay}>
      <FormFieldLayout
        fieldId={fieldId}
        label={label}
        errorMessage={errorMessage}
        showPlaceholderAndHideLabel={showPlaceholderAndHideLabel}
        isRequired={isRequired || needToPickCountry}
        explanation={
          needToPickCountry ? (
            <FormattedHTMLMessage
              id="chooseCountry"
              defaultMessage="Choose a country before searching"
            />
          ) : (
            explanation
          )
        }
        containerId={`${labelHtmlId}-id`}
      >
        {showSelectedOrgBox && value.id ? (
          <SelectedOrg
            org={value}
            onClear={() => {
              onChange(undefined);
              setAutoFocus(true);
            }}
          />
        ) : (
          <TypeaheadComponent
            className={errorMessage ? "sid-text-input--error" : ""}
            disabled={needToPickCountry || disabled}
            countryCode={countryChoiceValue}
            minimumSearchValueLength={getOptions().minimumOrganizationSearchLength}
            onChange={onChange}
            placeholder={placeholder}
            programId={programId}
            value={value}
            inputHtmlId={labelHtmlId}
            openOrgSearchEnabled={programTheme.openOrgSearchEnabled}
            locale={locale}
            orgSearchUrl={orgSearchUrl || getSafe(() => programTheme.config.orgSearchUrl)}
            urlAddSchoolForm={urlAddSchoolForm}
            isRequired={isRequired}
            isErrored={!!errorMessage}
            ariaDescribedBy={errorMessage ? errorHtmlId : ""}
            autoFocus={autoFocus}
          />
        )}
      </FormFieldLayout>
    </SearchOverlay>
  );
};

// DO NOT INJECT INTL. Localize your strings in the calling component and pass them as props to this component.
export const FormFieldTypeaheadComponent = FormFieldTypeahead;
